import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  message,
} from "lib/ui";
import { Settings2, Globe, Mail, Bell } from "lucide-react";
import axios from "axios";
import ApiPath from "../../../hooks/ApiFetch";
import {
  AdminPageLayout,
  AdminHero,
  AdminFormPanel,
  AdminFormSection,
} from "../../../components/admin/AdminPageShell";

const { Option } = Select;

const ApplicationPage = () => {
  axios.defaults.withCredentials = true;
  const [form] = Form.useForm();
  const [docId, setDocId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await ApiPath.get("/application");
      const doc = Array.isArray(res.data) ? res.data[0] : res.data;
      if (doc?._id) {
        setDocId(doc._id);
        form.setFieldsValue(doc);
      }
    } catch {
      /* optional endpoint */
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      if (docId) {
        await ApiPath.put(`/application/${docId}`, values);
      } else {
        const res = await ApiPath.post("/application", values);
        setDocId(res.data?._id);
      }
      message.success("Application settings saved");
    } catch {
      message.error("Could not save application settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageLayout>
      <AdminHero
        icon={Settings2}
        badge="Platform"
        title="Application"
        description="Timezone, email delivery, and push notification keys for customer, vendor, and driver apps."
      />

      <AdminFormPanel
        title="App configuration"
        description="Changes apply to all connected mobile and web clients."
        footer={
          <Button
            type="primary"
            htmlType="submit"
            form="app-config-form"
            loading={saving}
            size="large"
          >
            Save configuration
          </Button>
        }
      >
        <Form
          id="app-config-form"
          form={form}
          onFinish={onFinish}
          layout="vertical"
          disabled={loading}
          className="space-y-8"
        >
          <AdminFormSection title="General">
            <Form.Item label="Timezone" name="app_timezone">
              <Select placeholder="Select timezone" className="w-full">
                <Option value="Asia/Dubai">Asia/Dubai</Option>
                <Option value="Asia/Bangkok">Asia/Bangkok</Option>
                <Option value="UTC">UTC</Option>
              </Select>
            </Form.Item>
          </AdminFormSection>

          <AdminFormSection title="Email">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="From address" name="MAIL_FROM_ADDRESS">
                  <Input placeholder="noreply@tidyhub.ae" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="From name" name="MAIL_FROM_NAME">
                  <Input placeholder="Tidyhub" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Mail driver" name="mail_driver">
              <Select placeholder="Select driver" className="w-full">
                <Option value="mailgun">Mailgun</Option>
                <Option value="smtp">SMTP</Option>
              </Select>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Mailgun domain" name="mailgun_domain">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Mailgun secret" name="mailgun_secret">
                  <Input type="password" />
                </Form.Item>
              </Col>
            </Row>
          </AdminFormSection>

          <AdminFormSection title="Push notifications (OneSignal)">
            <div className="mb-4 flex items-center gap-2 text-xs text-slate-500">
              <Bell size={14} />
              Separate keys per app role
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Customer app ID"
                  name="ONESIGNAL_APP_ID_CUSTOMER"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Customer REST API"
                  name="ONESIGNAL_REST_API_CUSTOMER"
                >
                  <Input type="password" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Vendor app ID" name="ONESIGNAL_APP_ID_VENDOR">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Vendor REST API"
                  name="ONESIGNAL_REST_API_VENDOR"
                >
                  <Input type="password" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Driver app ID" name="ONESIGNAL_APP_ID_DRIVER">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Driver REST API"
                  name="ONESIGNAL_REST_API_DRIVER"
                >
                  <Input type="password" />
                </Form.Item>
              </Col>
            </Row>
          </AdminFormSection>
        </Form>
      </AdminFormPanel>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { icon: Globe, text: "Timezone affects order cut-offs and delivery slots." },
          { icon: Mail, text: "Email keys power order confirmations and OTP messages." },
          { icon: Bell, text: "Push keys notify customers, vendors, and drivers." },
        ].map(({ icon: Icon, text }) => (
          <div
            key={text}
            className="flex gap-3 rounded-xl border border-slate-200/80 bg-white p-4 text-sm text-slate-600 shadow-card"
          >
            <Icon size={18} className="shrink-0 text-brand" />
            {text}
          </div>
        ))}
      </div>
    </AdminPageLayout>
  );
};

export default ApplicationPage;
